import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Container, Row, Col, Spinner, Alert, Form, Button, Modal } from "react-bootstrap";
import { SectionLoader } from "../../Components/LoadingSpinner";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { Building as BuildingIcon, Filter, X, Trash2, FileText, Check, ClipboardCheck, RefreshCw, Flag, CalendarCheck, Star, Wrench, AlertTriangle, Plus, ChevronDown } from "lucide-react";
import { getDatabase, ref, get, set, push, remove } from "firebase/database";
import { useParams } from "react-router-dom";
import { BuildingSummary, BuildingModel, Building } from "../../types/protocols";
import { BreadcrumbComponent } from "../../Components/BreadCrumb";
import { getAuth } from "firebase/auth";
import { uploadTimelineAttachment, deleteTimelineAttachment, deleteAllTimelineAttachments } from "../../Helpers/TimelineAttachmentUtils";
import { formatDateForStorage } from "../../Helpers/DateUtils";
import { normalizeLineBreaks } from "../../Helpers/FormatUtils";
import { TimelineEventType, TimelineEvent, CustomTimelineItem, TimelineAttachment, getEventColor, getEventTypeLabel, getEventDescription, isWorkOrderEventType } from "../../types/timeline";
import TimelineAttachments from "../../Components/Timeline/TimelineAttachments";
import TimelineEventDetailModal from "../../Components/Timeline/TimelineEventDetailModal";
import WorkOrderDetailModal from "../Förvaltning/components/WorkOrderDetailModal";
import ComponentPickerModal from "../Förvaltning/components/ComponentPickerModal";
import ReportDraftsManagerModal from "../../Components/Timeline/ReportDraftsManagerModal";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { useIsSmallScreen } from "../../hooks/useIsSmallScreen";
import { UploadPickerModal } from "../../Components/UploadPickerModal";
import { BaseComponentImpl } from "../../types/implementations";
import { useFirebaseImage, getImagePathWithSize, ImageSize } from "../../Helpers/ImageLoader";
import { getCoverPhotoStoragePath } from "../../Helpers/utils";
import { placeholderForBuildingType } from "../../Helpers/PlaceholderUtils";
import { useDragDrop } from "../../hooks/useDragDrop";
import { useTimelineEvents } from "../../hooks/timeline/useTimelineEvents";
import { useUsersData, Userprofile } from "../../Helpers/UsersContext";
import { useBuildingsData } from "../../Helpers/BuildingSummariesContext";
import "./TimelinePage.css";
import NumberInput from '../../Components/NumberInput';
import ToggleSwitch from '../../Components/ToggleSwitch';
import ConfirmationModal from '../../Components/ConfirmationModal';
import { applyComponentPatch } from '../../Helpers/componentPatch';
import { recordActivity } from '../../api/activityApi';
import { getEventId } from '../../utils/timelineEventIds';

const isPlausibleYear = (year: number): boolean => year >= 1800 && year <= 2100;

// Rows pop in one after another on load; past this many rows the rest pop in
// together, so a long list (or rows a filter brings back) isn't held back for seconds.
const ROW_STAGGER_LIMIT = 20;
const ROW_STAGGER_MS = 25;
// Last row's delay plus the longest pop (connector: 40ms offset + 150ms), with margin
const ROW_ANIMATION_MS = ROW_STAGGER_LIMIT * ROW_STAGGER_MS + 300;

// Component for rendering event content with building thumbnail
const getEventIcon = (eventType: TimelineEventType) => {
    switch (eventType) {
        case TimelineEventType.WORK_ORDER_MAINTENANCE: return <Wrench size={14} color="white" />;
        case TimelineEventType.WORK_ORDER_ISSUE:       return <AlertTriangle size={14} color="white" />;
        case TimelineEventType.WORK_ORDER_OTHER:       return <Check size={14} color="white" />;
        case TimelineEventType.INSPECTION_COMPLETED:   return <ClipboardCheck size={14} color="white" />;
        case TimelineEventType.COMPONENT_REPLACED:     return <RefreshCw size={14} color="white" />;
        case TimelineEventType.PROJECT_END:            return <Flag size={14} color="white" />;
        case TimelineEventType.MAINTENANCE_PLAN_ACTIVATED: return <CalendarCheck size={14} color="white" />;
        case TimelineEventType.CUSTOM:                 return <Star size={14} color="white" />;
        default:                                       return null;
    }
};

interface TimelineEventContentProps {
    event: TimelineEvent;
    showBuildingInfo: boolean;
    onClick: () => void;
}

const TimelineEventContent: React.FC<TimelineEventContentProps> = ({ event, showBuildingInfo, onClick }) => {
    const eventColor = getEventColor(event.eventType);
    const coverPhotoPath = showBuildingInfo ? getCoverPhotoStoragePath(event.buildingSummary) : undefined;
    const thumbnailPath = getImagePathWithSize(coverPhotoPath, ImageSize.THUMBNAIL_SMALL);
    const placeholder = placeholderForBuildingType(event.buildingSummary?.buildingType);
    const { src: coverPhotoSrc, isLoading } = useFirebaseImage(thumbnailPath, placeholder);

    // Project items show their status (e.g. "Utförd") so completed projects are
    // distinguishable from planned/ongoing ones in the list.
    const projectStatus = event.eventType === TimelineEventType.PROJECT_END ? event.projectStatus : undefined;

    // Work orders that belong to a project show the project name alongside the
    // building name, so the row tells you which project the work was part of.
    const workOrderProjectTitle = isWorkOrderEventType(event.eventType) ? event.projectTitle : undefined;
    const showTopRow = showBuildingInfo || projectStatus || workOrderProjectTitle;

    // Custom events ("Egen händelse") show only their title in the row; reveal the
    // full body on hover as a tooltip, matching the maintenance page tooltips.
    const customBody = event.eventType === TimelineEventType.CUSTOM ? event.description?.trim() : undefined;

    return (
        <div
            className={`timeline-item-text timeline-item-clickable ${showBuildingInfo ? 'with-thumbnail' : ''} ${customBody ? 'timeline-event-has-tooltip' : ''}`}
            style={{ backgroundColor: eventColor }}
            onClick={onClick}
        >
            {showBuildingInfo && (
                <div
                    className="timeline-building-thumbnail"
                    style={{
                        backgroundColor: isLoading ? 'rgba(255,255,255,0.3)' : undefined,
                        overflow: 'hidden'
                    }}
                >
                    {!isLoading && (
                        <img
                            src={coverPhotoSrc}
                            alt={event.buildingName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    )}
                </div>
            )}
            <div className="timeline-event-text-content">
                {showTopRow && (
                    <div className="timeline-building-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5em', flexWrap: 'wrap' }}>
                        {showBuildingInfo && <span>{event.buildingName}</span>}
                        {workOrderProjectTitle && (
                            <>
                                {showBuildingInfo && <span aria-hidden="true">•</span>}
                                <span>{workOrderProjectTitle}</span>
                            </>
                        )}
                        {projectStatus && (
                            <span className="timeline-status-pill" style={{ color: eventColor }}>
                                {projectStatus}
                            </span>
                        )}
                    </div>
                )}
                <span className="timeline-event-line" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4em' }}>
                    {getEventIcon(event.eventType)}
                    <span className="timeline-event-description-text">{getEventDescription(event)}</span>
                </span>
            </div>
            {customBody && (
                <span className="timeline-event-tooltip" role="tooltip">{customBody}</span>
            )}
        </div>
    );
};

const TimelinePage = () => {
    const { buildingKey } = useParams<{ buildingKey?: string }>();
    // Timeline events are synthesized in the shared hook (also used by the
    // report draft editor). reload({silent:true}) refreshes in place after
    // saving/deleting a custom event without blanking the timeline.
    const { allEvents, buildingSummary, loading, error, reload: reloadTimeline } = useTimelineEvents(buildingKey);

    // Check if viewing single building or all buildings
    const isSingleBuildingView = !!buildingKey;

    // Errors from page actions (saving/deleting events, attachments); fetch
    // errors come from the hook. Rendered through the same alert.
    const [actionError, setActionError] = useState<string | null>(null);
    
    // Rows pop in after a (non-silent) load only. The class is dropped once the
    // animation has run, so rows a filter brings back appear at once.
    const [animateRows, setAnimateRows] = useState<boolean>(true);
    useEffect(() => {
        if (loading) {
            setAnimateRows(true);
            return;
        }
        const timer = setTimeout(() => setAnimateRows(false), ROW_ANIMATION_MS);
        return () => clearTimeout(timer);
    }, [loading]);

    // Filter state
    const [selectedEventTypes, setSelectedEventTypes] = useState<Set<TimelineEventType>>(
        new Set(Object.values(TimelineEventType))
    );
    const [startYear, setStartYear] = useState<number>(new Date().getFullYear());
    const [endYear, setEndYear] = useState<number>(new Date().getFullYear());
    const [showAllYears, setShowAllYears] = useState<boolean>(false);
    // Phones fold the filter pane behind a toggle so the events start near the top.
    const isSmallScreen = useIsSmallScreen();
    const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
    const filterToggleRef = useRef<HTMLButtonElement>(null);

    // "OK" in the phone filter pane: fold it away and bring the toggle back into
    // view, since the user has usually scrolled down to reach the button.
    const closeFilters = () => {
        setFiltersOpen(false);
        filterToggleRef.current?.scrollIntoView({ block: 'nearest' });
    };
    
    // Custom timeline item state
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [editingEventId, setEditingEventId] = useState<string | null>(null);
    // Id of the custom event pending deletion; non-null shows the confirm modal.
    const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
    // Which building a custom event belongs to. In single-building view this is the
    // route's buildingKey; in the all-buildings view the user picks it in the modal.
    const [selectedBuildingKey, setSelectedBuildingKey] = useState<string>('');
    const [newItemTitle, setNewItemTitle] = useState<string>('');
    const [newItemDescription, setNewItemDescription] = useState<string>('');
    const [newItemDate, setNewItemDate] = useState<string>('');
    const [newItemYear, setNewItemYear] = useState<string>('');
    const [inexactDate, setInexactDate] = useState<boolean>(false);
    // Components linked to the custom event being created/edited (optional).
    const [newItemComponentKeys, setNewItemComponentKeys] = useState<string[]>([]);
    const [showComponentPicker, setShowComponentPicker] = useState<boolean>(false);
    // Full building model (structure/components) loaded on demand for the picker.
    const [pickerBuilding, setPickerBuilding] = useState<Building | null>(null);
    const [loadingPickerBuilding, setLoadingPickerBuilding] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [existingAttachments, setExistingAttachments] = useState<{ [key: string]: TimelineAttachment }>({});

    const [showUploadModal, setShowUploadModal] = useState(false);

    // Handle selected/dropped files
    const handleFiles = useCallback((files: File[]) => {
        setPendingFiles(prev => [...prev, ...files]);
    }, []);

    const { isDragOver, dragHandlers } = useDragDrop({ onFilesDropped: handleFiles });
    const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
    const [removingAttachments, setRemovingAttachments] = useState<Set<string>>(new Set());
    const [attachmentToRemove, setAttachmentToRemove] = useState<string | null>(null);

    // State for event detail modal
    const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
    const [showEventDetailModal, setShowEventDetailModal] = useState<boolean>(false);

    // State for work order detail modal (reused from Förvaltning)
    const [viewingWorkOrderKey, setViewingWorkOrderKey] = useState<string | null>(null);

    // UID → display name lookup for the work order modal
    const { users } = useUsersData();
    const userNameMap = useMemo(() => {
        const map = new Map<string, string>();
        users?.forEach((u: Userprofile) => {
            map.set(u.key, u.name || u.key);
        });
        return map;
    }, [users]);
    const lookupUserName = useCallback((uid: string): string | undefined => userNameMap.get(uid), [userNameMap]);
    const getUserName = useCallback((uid: string): string => {
        return userNameMap.get(uid) || uid;
    }, [userNameMap]);

    // State for the report drafts manager (admin-only)
    const [showDraftsModal, setShowDraftsModal] = useState<boolean>(false);
    const isAdmin = useIsAdmin();

    const isEditMode = !!editingEventId;

    // Building list for the picker shown when adding an event from the all-buildings view
    const { buildings } = useBuildingsData();
    const buildingOptions = useMemo(() => {
        return (buildings || [])
            .map(b => ({ key: b.key, name: b.name || 'Namnlös byggnad' }))
            .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    }, [buildings]);

    // The building a custom event is written to: the route's building in single-building
    // view, otherwise the one selected in the modal.
    const effectiveBuildingKey = buildingKey || selectedBuildingKey;

    // Load the full building model (structure/components) for the component picker
    // whenever the add/edit modal is open for a specific building. Kept separate from
    // the main timeline fetch so it loads on demand (e.g. after picking a building in
    // the all-buildings view) without re-running the whole timeline query.
    useEffect(() => {
        if (!showAddModal || !effectiveBuildingKey) {
            setPickerBuilding(null);
            return;
        }

        let cancelled = false;
        const loadPickerBuilding = async () => {
            setLoadingPickerBuilding(true);
            try {
                const db = getDatabase();
                const buildingPath = `buildings/${effectiveBuildingKey}`;
                const [summarySnap, structureSnap, mapImagesSnap, currentMapKeySnap] = await Promise.all([
                    get(ref(db, `buildingsSummary/${effectiveBuildingKey}`)),
                    get(ref(db, `${buildingPath}/structure`)),
                    get(ref(db, `${buildingPath}/mapImages`)),
                    get(ref(db, `${buildingPath}/currentMapImageKey`)),
                ]);
                if (cancelled) return;
                if (summarySnap.exists()) {
                    setPickerBuilding({
                        key: effectiveBuildingKey,
                        model: {
                            structure: structureSnap.exists() ? structureSnap.val() : undefined,
                            mapImages: mapImagesSnap.exists() ? mapImagesSnap.val() : undefined,
                            currentMapImageKey: currentMapKeySnap.exists() ? currentMapKeySnap.val() : undefined,
                        } as BuildingModel,
                        summary: summarySnap.val() as BuildingSummary,
                    });
                } else {
                    setPickerBuilding(null);
                }
            } catch (err) {
                console.error('Failed to load building for component picker:', err);
            } finally {
                if (!cancelled) setLoadingPickerBuilding(false);
            }
        };

        loadPickerBuilding();
        return () => { cancelled = true; };
    }, [showAddModal, effectiveBuildingKey]);

    // Resolve a linked component's display name from the loaded picker building model.
    const pickerComponentDisplayName = useCallback((componentKey: string): string => {
        const data = pickerBuilding?.model?.structure?.components?.[componentKey];
        if (data) {
            return new BaseComponentImpl({ ...data, key: componentKey }).displayName({} as Building);
        }
        return 'Komponent';
    }, [pickerBuilding]);

    // Set document title
    useDocumentTitle(
        isSingleBuildingView && buildingSummary
            ? `${buildingSummary.name || 'Byggnad'} - Tidslinje`
            : 'Tidslinje'
    );

    // Default filters after each (re)load, based on view mode
    useEffect(() => {
        if (allEvents.length > 0) {
            // Find the maximum year from all events (to show future events by default)
            const currentYear = new Date().getFullYear();
            const validYears = allEvents
                .map(event => event.date.getFullYear())
                .filter(year => !isNaN(year));
            const maxYear = validYears.length > 0 ? Math.max(...validYears) : currentYear;

            // Use the maximum year found, or current year if no future events
            setStartYear(currentYear - 1);
            setEndYear(Math.max(maxYear, currentYear));

            // For single building view, show all years by default
            if (isSingleBuildingView) {
                setShowAllYears(true);
            }
        }
    }, [allEvents, isSingleBuildingView]);

    // Filter events based on selected criteria
    const filteredEvents = useMemo(() => {
        return allEvents.filter(event => {
            // Filter by event type
            if (!selectedEventTypes.has(event.eventType)) {
                return false;
            }
            
            // Filter by year range (only if not showing all years)
            if (!showAllYears) {
                const eventYear = event.date.getFullYear();
                if (eventYear < startYear || eventYear > endYear) {
                    return false;
                }
            }
            
            return true;
        });
    }, [allEvents, selectedEventTypes, startYear, endYear, showAllYears]);

    // Build timeline items array with year markers inserted
    type TimelineRow =
        | { type: 'event'; event: TimelineEvent; key: string }
        | { type: 'year'; year: number };

    const timelineRows = useMemo((): TimelineRow[] => {
        const rows: TimelineRow[] = [];
        // Rows are keyed by event identity, not position, so toggling a filter
        // keeps the remaining rows mounted instead of rebuilding (and
        // re-animating, re-loading thumbnails for) the whole list. A repeated
        // id (e.g. a work order in both active and archive) gets a suffix.
        const keyCounts = new Map<string, number>();

        filteredEvents.forEach((event, index) => {
            const id = getEventId(event);
            const seen = keyCounts.get(id) ?? 0;
            keyCounts.set(id, seen + 1);
            rows.push({ type: 'event', event, key: seen === 0 ? id : `${id}#${seen}` });

            // Events are sorted newest-first, so a year's oldest event is the one
            // right before the year changes (or the list ends). Place the year
            // label below that event so each year's events sit above their label.
            const currentYear = event.date.getFullYear();
            const nextEvent = filteredEvents[index + 1];
            const nextYear = nextEvent ? nextEvent.date.getFullYear() : null;

            if (nextYear === null || nextYear !== currentYear) {
                rows.push({ type: 'year', year: currentYear });
            }
        });

        return rows;
    }, [filteredEvents]);

    // Count events filtered out by year range only (not by event type)
    const eventsFilteredByYear = useMemo(() => {
        if (showAllYears) return 0;
        
        return allEvents.filter(event => {
            // Only count if event type is selected
            if (!selectedEventTypes.has(event.eventType)) {
                return false;
            }
            
            // Check if filtered out by year
            const eventYear = event.date.getFullYear();
            return eventYear < startYear || eventYear > endYear;
        }).length;
    }, [allEvents, selectedEventTypes, startYear, endYear, showAllYears]);

    // Build a map of building summaries from all events for the export modal
    const buildingSummariesMap = useMemo(() => {
        const map = new Map<string, BuildingSummary>();
        allEvents.forEach(event => {
            if (event.buildingSummary && !map.has(event.buildingKey)) {
                map.set(event.buildingKey, event.buildingSummary);
            }
        });
        return map;
    }, [allEvents]);

    // Short date format for timeline markers: "1 jan", "27 jun", etc.
    const formatMarkerDate = (date: Date): { day: string; month: string } => {
        const day = date.getDate().toString();
        const month = date.toLocaleDateString('sv-SE', { month: 'short' }).replace('.', '');
        return { day, month };
    };


    // Handle clicking on timeline events - open the read-only presentation modal.
    // Custom events get an "Edit" action there that opens the edit modal.
    const handleEventClick = (event: TimelineEvent) => {
        if (isWorkOrderEventType(event.eventType) && event.workOrderKey) {
            // For work orders, reuse the full-featured modal from Förvaltning
            setViewingWorkOrderKey(event.workOrderKey);
        } else {
            // All other events (incl. custom) open the presentation/detail modal first
            setSelectedEvent(event);
            setShowEventDetailModal(true);
        }
    };

    // From the presentation modal's "Redigera" action: close it and open the edit modal.
    const handleEditFromDetail = (event: TimelineEvent) => {
        handleCloseEventDetailModal();
        handleOpenEditModal(event);
    };

    // Close event detail modal
    const handleCloseEventDetailModal = () => {
        setShowEventDetailModal(false);
        setSelectedEvent(null);
    };

    const handleEventTypeToggle = (eventType: TimelineEventType) => {
        const newSelection = new Set(selectedEventTypes);
        if (newSelection.has(eventType)) {
            newSelection.delete(eventType);
        } else {
            newSelection.add(eventType);
        }
        setSelectedEventTypes(newSelection);
    };

    // One-line summary on the phone filter toggle, so the active filters are
    // visible while the pane is folded away.
    const filterSummary = useMemo(() => {
        const totalTypes = Object.values(TimelineEventType).length;
        const types = selectedEventTypes.size === totalTypes
            ? 'Alla typer'
            : `${selectedEventTypes.size} av ${totalTypes} typer`;
        const period = showAllYears
            ? 'Hela perioden'
            : startYear === endYear ? `${startYear}` : `${startYear}–${endYear}`;
        return `${types} · ${period}`;
    }, [selectedEventTypes, showAllYears, startYear, endYear]);

    const handleResetFilters = () => {
        setSelectedEventTypes(new Set(Object.values(TimelineEventType)));

        // Find the maximum year from all events (to show future events by default)
        const currentYear = new Date().getFullYear();
        if (allEvents.length > 0) {
            const validYears = allEvents
                .map(event => event.date.getFullYear())
                .filter(year => !isNaN(year));
            const maxYear = validYears.length > 0 ? Math.max(...validYears) : currentYear;
            const defaultEndYear = Math.max(maxYear, currentYear);
            const defaultStartYear = currentYear - 1;
            setStartYear(defaultStartYear);
            setEndYear(defaultEndYear);
        } else {
            const lastYear = currentYear - 1;
            setStartYear(lastYear);
            setEndYear(currentYear);
        }

        // For single building view, default to showing all years
        // For all buildings view, default to last year
        setShowAllYears(isSingleBuildingView);
    };

    const handleOpenAddModal = () => {
        setEditingEventId(null);
        // Pre-select the route's building (single-building view); leave empty so the
        // user must pick one in the all-buildings view.
        setSelectedBuildingKey(buildingKey || '');
        setNewItemTitle('');
        setNewItemDescription('');
        setNewItemDate('');
        setNewItemYear('');
        setInexactDate(false);
        setNewItemComponentKeys([]);
        setPendingFiles([]);
        setExistingAttachments({});
        setShowAddModal(true);
    };

    const handleOpenEditModal = async (event: TimelineEvent) => {
        if (!event.customEventId || !event.buildingKey) return;

        setEditingEventId(event.customEventId);
        setSelectedBuildingKey(event.buildingKey);
        setNewItemTitle(event.title || '');
        setNewItemDescription(event.description || '');
        setInexactDate(event.inexactDate || false);
        setNewItemComponentKeys(event.linkedComponents?.map(c => c.key) || []);

        // Extract date from displayDate (YYYY-MM-DD hh:mm:ss)
        const datePart = event.displayDate.split(' ')[0];
        const yearPart = datePart.split('-')[0];
        
        if (event.inexactDate) {
            setNewItemYear(yearPart);
            setNewItemDate('');
        } else {
            setNewItemDate(datePart);
            setNewItemYear('');
        }
        
        // Load existing attachments
        const db = getDatabase();
        const attachmentsRef = ref(db, `buildings/${event.buildingKey}/timelineItems/${event.customEventId}/attachments`);
        try {
            const attachmentsSnapshot = await get(attachmentsRef);
            if (attachmentsSnapshot.exists()) {
                setExistingAttachments(attachmentsSnapshot.val() as { [key: string]: TimelineAttachment });
            } else {
                setExistingAttachments({});
            }
        } catch (error) {
            console.error('Error loading attachments:', error);
            setExistingAttachments({});
        }
        
        setPendingFiles([]);
        setShowAddModal(true);
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
        setEditingEventId(null);
        setSelectedBuildingKey('');
        setNewItemTitle('');
        setNewItemDescription('');
        setNewItemDate('');
        setNewItemYear('');
        setInexactDate(false);
        setNewItemComponentKeys([]);
        setShowComponentPicker(false);
        setPendingFiles([]);
        setExistingAttachments({});
        setUploadingFiles(new Set());
        setRemovingAttachments(new Set());
    };

    const handleRemovePendingFile = (index: number) => {
        setPendingFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleRemoveExistingAttachment = (attachmentKey: string) => {
        setActionError(null);
        setAttachmentToRemove(attachmentKey);
    };

    const attachmentToRemoveName = (() => {
        const attachment = attachmentToRemove ? existingAttachments[attachmentToRemove] : undefined;
        return attachment && 'fileName' in attachment && typeof attachment.fileName === 'string'
            ? attachment.fileName
            : undefined;
    })();

    const confirmRemoveExistingAttachment = async () => {
        const attachmentKey = attachmentToRemove;
        setAttachmentToRemove(null);
        if (!attachmentKey || !effectiveBuildingKey || !editingEventId) return;

        setRemovingAttachments(prev => new Set(prev).add(attachmentKey));
        try {
            const result = await deleteTimelineAttachment(effectiveBuildingKey, editingEventId, attachmentKey);
            if (result.success) {
                setExistingAttachments(prev => {
                    const newAttachments = { ...prev };
                    delete newAttachments[attachmentKey];
                    return newAttachments;
                });
            } else {
                setActionError(result.error || 'Kunde inte ta bort bilagan');
            }
        } catch (error) {
            console.error('Error removing attachment:', error);
            setActionError('Kunde inte ta bort bilagan');
        } finally {
            setRemovingAttachments(prev => {
                const newSet = new Set(prev);
                newSet.delete(attachmentKey);
                return newSet;
            });
        }
    };

    const handleSaveCustomItem = async () => {
        setActionError(null);
        if (!effectiveBuildingKey || !newItemTitle.trim() || !newItemDescription.trim()) {
            return;
        }

        // Validate date or year depending on inexactDate flag
        if (inexactDate) {
            if (!newItemYear || !newItemYear.trim()) {
                return;
            }
        } else {
            if (!newItemDate) {
                return;
            }
        }

        try {
            setSaving(true);
            const auth = getAuth();
            const user = auth.currentUser;
            
            if (!user) {
                setActionError('Du måste vara inloggad för att lägga till händelser');
                return;
            }

            // Parse and format the date
            let dateString: string;
            if (inexactDate) {
                // For inexact dates, use January 1st of the given year
                const year = parseInt(newItemYear.trim());
                if (isNaN(year) || year < 1000 || year > 9999) {
                    setActionError('Ogiltigt år');
                    return;
                }
                dateString = `${year}-01-01 12:00:00`;
            } else {
                // Parse the date input (yyyy-mm-dd) and add time
                const dateParts = newItemDate.split('-');
                if (dateParts.length !== 3) {
                    setActionError('Ogiltigt datumformat');
                    return;
                }
                dateString = `${newItemDate} 12:00:00`; // Default to noon
            }

            const db = getDatabase();
            let timelineItemId: string;

            // Linked components are optional; trim, dedupe and drop empties.
            const cleanComponentKeys = Array.from(
                new Set(newItemComponentKeys.map(k => k.trim()).filter(Boolean))
            );

            if (isEditMode && editingEventId) {
                // Update existing item
                timelineItemId = editingEventId;
                const itemRef = ref(db, `buildings/${effectiveBuildingKey}/timelineItems/${editingEventId}`);
                const snapshot = await get(itemRef);

                if (snapshot.exists()) {
                    const existingItem = snapshot.val() as CustomTimelineItem;
                    const updatedItem: CustomTimelineItem = {
                        ...existingItem,
                        title: newItemTitle.trim(),
                        description: normalizeLineBreaks(newItemDescription).trim(),
                        date: dateString,
                        inexactDate: inexactDate
                    };
                    // Set the links when present; clear the field entirely when none remain.
                    if (cleanComponentKeys.length > 0) {
                        updatedItem.componentKeys = cleanComponentKeys;
                    } else {
                        delete updatedItem.componentKeys;
                    }
                    await set(itemRef, updatedItem);
                    recordActivity('timelineItem.updated', { buildingKey: effectiveBuildingKey, targetKey: editingEventId });
                }
            } else {
                // Create new item
                const now = new Date();
                const createdAtString = formatDateForStorage(now);
                const newItemRef = push(ref(db, `buildings/${effectiveBuildingKey}/timelineItems`));
                timelineItemId = newItemRef.key!;

                const customItem: CustomTimelineItem = {
                    title: newItemTitle.trim(),
                    description: normalizeLineBreaks(newItemDescription).trim(),
                    date: dateString,
                    createdBy: user.uid,
                    createdAt: createdAtString,
                    inexactDate: inexactDate,
                    ...(cleanComponentKeys.length > 0 ? { componentKeys: cleanComponentKeys } : {})
                };

                await set(newItemRef, customItem);
                recordActivity('timelineItem.created', { buildingKey: effectiveBuildingKey, targetKey: timelineItemId });
            }

            // Upload pending files
            if (pendingFiles.length > 0) {
                setUploadingFiles(new Set(pendingFiles.map((_, i) => `pending-${i}`)));
                try {
                    const uploadPromises = pendingFiles.map(async (file) => {
                        const result = await uploadTimelineAttachment(
                            effectiveBuildingKey,
                            timelineItemId,
                            file,
                            user.uid
                        );
                        if (!result.success) {
                            throw new Error(result.error || 'Upload failed');
                        }
                        return result.attachment;
                    });

                    await Promise.all(uploadPromises);
                } catch (uploadError) {
                    console.error('Error uploading files:', uploadError);
                    setActionError('Några filer kunde inte laddas upp. Försök igen.');
                    setSaving(false);
                    return;
                } finally {
                    setUploadingFiles(new Set());
                }
            }

            // Reset form and close modal
            handleCloseModal();

            // Refresh the timeline in place (re-runs the fetch effect)
            reloadTimeline({ silent: true });
        } catch (err) {
            console.error('Failed to save custom timeline item', err);
            setActionError('Det gick inte att spara händelsen. Försök igen senare.');
        } finally {
            setSaving(false);
        }
    };

    // Open the confirmation modal for removing a custom event.
    const requestDeleteCustomItem = (eventId?: string) => {
        const idToDelete = eventId || editingEventId;
        if (!effectiveBuildingKey || !idToDelete) return;
        setDeleteTargetId(idToDelete);
    };

    // Perform the deletion once the user confirms in the modal.
    const confirmDeleteCustomItem = async () => {
        setActionError(null);
        const idToDelete = deleteTargetId;
        if (!effectiveBuildingKey || !idToDelete) return;

        try {
            setSaving(true);
            const db = getDatabase();

            // Get attachments before deleting
            const attachmentsRef = ref(db, `buildings/${effectiveBuildingKey}/timelineItems/${idToDelete}/attachments`);
            const attachmentsSnapshot = await get(attachmentsRef);

            // Delete all attachments from Storage
            if (attachmentsSnapshot.exists()) {
                const attachments = attachmentsSnapshot.val() as { [key: string]: TimelineAttachment };
                await deleteAllTimelineAttachments(effectiveBuildingKey, idToDelete, attachments);
            }

            // Delete timeline item (this will also delete attachments from RTDB)
            const itemRef = ref(db, `buildings/${effectiveBuildingKey}/timelineItems/${idToDelete}`);
            await remove(itemRef);
            recordActivity('timelineItem.deleted', { buildingKey: effectiveBuildingKey, targetKey: idToDelete });

            // Close the edit modal if it's open
            if (showAddModal) {
                handleCloseModal();
            }

            // Refresh the timeline in place (re-runs the fetch effect)
            reloadTimeline({ silent: true });
        } catch (err) {
            console.error('Failed to delete custom timeline item', err);
            setActionError('Det gick inte att ta bort händelsen. Försök igen senare.');
        } finally {
            setSaving(false);
            setDeleteTargetId(null);
        }
    };

    return (
    <div className="timeline-page">
        <div style={{ flexShrink: 0 }}>
            <BreadcrumbComponent
                items={
                    isSingleBuildingView && buildingSummary
                        ? [
                            { text: buildingSummary.name || 'Byggnad', link: `/building/${buildingKey}` },
                            { text: 'Tidslinje' }
                        ]
                        : [{ text: 'Tidslinje' }]
                }
            />
        </div>

        <div className="timeline-page-header" style={{ marginBottom: 0, marginTop: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h1 className="timeline-page-title page-title">
                        {isSingleBuildingView && buildingSummary?.name
                            ? `Tidslinje för ${buildingSummary.name}`
                            : 'Tidslinje samtliga byggnader'}
                    </h1>
                </div>
            </div>
        </div>

        <div className="timeline-layout">
            {isSmallScreen && (
                <button
                    type="button"
                    ref={filterToggleRef}
                    className="timeline-filter-toggle"
                    aria-expanded={filtersOpen}
                    aria-controls="timeline-filter-pane"
                    onClick={() => setFiltersOpen(open => !open)}
                >
                    <Filter size={16} aria-hidden="true" />
                    <span className="timeline-filter-toggle-label">Filter</span>
                    <span className="timeline-filter-toggle-summary">{filterSummary}</span>
                    <ChevronDown size={18} className="timeline-filter-toggle-chevron" aria-hidden="true" />
                </button>
            )}
            <Container className="timeline-container std-box dropshadow">
                <Row>
                    <Col>

                        {loading && <SectionLoader />}

                        {(error || actionError) && (
                            <Alert variant="danger">
                                {error || actionError}
                            </Alert>
                        )}

                        {!loading && !error && (
                            <div className="mb-4 d-flex flex-wrap gap-2">
                                <Button
                                    variant="outline-primary"
                                    onClick={handleOpenAddModal}
                                >
                                    <Plus size={14} className="me-2" />
                                    Lägg till händelse
                                </Button>
                                {isAdmin && !isSingleBuildingView && (
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => setShowDraftsModal(true)}
                                    >
                                        <FileText size={14} className="me-2" />
                                        Exportera
                                    </Button>
                                )}
                            </div>
                        )}

                        {!loading && !error && filteredEvents.length === 0 && (
                            <div className="empty-state-card text-center">
                                    <BuildingIcon size={48} className="empty-state-icon mb-3" />
                                    <h3>Inga händelser att visa</h3>
                                    <p className="text-muted">
                                        {allEvents.length === 0 
                                            ? 'Det finns inga händelser att visa ännu.'
                                            : 'Inga händelser matchar de valda filtren. Prova att justera filtren.'}
                                    </p>
                                    {allEvents.length > 0 && (
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm"
                                            onClick={handleResetFilters}
                                            className="mt-3"
                                        >
                                            Återställ filter
                                        </Button>
                                    )}
                            </div>
                        )}

                        {!loading && !error && filteredEvents.length > 0 && (
                            <>
                            <div className={`timeline${animateRows ? ' timeline-animate' : ''}`}>
                                <div className="timeline-endpoint timeline-endpoint-start"></div>
                                <div className="timeline-endpoint timeline-endpoint-end"></div>
                                {timelineRows.map((row, index) => {
                                    const rowStyle = animateRows
                                        ? { '--row-delay': `${Math.min(index, ROW_STAGGER_LIMIT) * ROW_STAGGER_MS}ms` } as React.CSSProperties
                                        : undefined;

                                    if (row.type === 'year') {
                                        return (
                                            <div key={`year-${row.year}`} className="timeline-item" style={rowStyle}>
                                                <div className="timeline-marker">
                                                    <div className="timeline-marker-dot timeline-year-dot">
                                                        <span className="timeline-marker-year">{row.year}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }

                                    const event = row.event;
                                    return (
                                        <div key={row.key} className={`timeline-item${event.inexactDate ? ' timeline-item-inexact' : ''}`} style={rowStyle}>
                                            <div className="timeline-marker">
                                                {event.inexactDate ? (
                                                    <div className="timeline-marker-node"></div>
                                                ) : (
                                                    <div className="timeline-marker-dot">
                                                        <span className="timeline-marker-day">{formatMarkerDate(event.date).day}</span>
                                                        <span className="timeline-marker-month">{formatMarkerDate(event.date).month}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="timeline-connector"></div>
                                            <div className="timeline-content">
                                                <TimelineEventContent
                                                    event={event}
                                                    showBuildingInfo={!buildingKey}
                                                    onClick={() => handleEventClick(event)}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            </>
                        )}

                        {/* Year filter info message */}
                        {!loading && !error && eventsFilteredByYear > 0 && (
                            <Alert variant="info" className="mt-4" style={{ backgroundColor: '#2D3142', color: 'white', borderColor: '#2D3142' }}>
                                <div className="d-flex align-items-center">
                                    <Filter size={14} className="me-2" />
                                    <span>
                                        {eventsFilteredByYear} {eventsFilteredByYear === 1 ? 'händelse är dold' : 'händelser är dolda'} på grund av tidsperiodfiltret ({startYear}–{endYear}).{' '}
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0 align-baseline"
                                            style={{ color: 'white' }}
                                            onClick={() => setShowAllYears(true)}
                                        >
                                            Visa hela perioden
                                        </Button>
                                    </span>
                                </div>
                            </Alert>
                        )}
                    </Col>
                </Row>
            </Container>

            {/* Filter Pane */}
            <div
                id="timeline-filter-pane"
                className="timeline-filter-pane std-box"
                hidden={isSmallScreen && !filtersOpen}
            >
                <h6 className="filter-section-title">Händelsetyper</h6>
                <div className="filter-checkboxes">
                    {Object.values(TimelineEventType).map(eventType => (
                        <label key={eventType} className="filter-toggle">
                            <ToggleSwitch
                                small
                                checked={selectedEventTypes.has(eventType)}
                                onChange={() => handleEventTypeToggle(eventType)}
                            />
                            <span
                                className="filter-color-dot"
                                style={{ backgroundColor: getEventColor(eventType) }}
                            ></span>
                            <span>{getEventTypeLabel(eventType)}</span>
                        </label>
                    ))}
                </div>

                <h6 className="filter-section-title mt-4">Tidsperiod</h6>
                <label className="filter-toggle mb-3">
                    <ToggleSwitch small checked={showAllYears} onChange={setShowAllYears} />
                    <span>Hela perioden</span>
                </label>
                <Form.Group className="mb-2">
                    <Form.Label>Från år</Form.Label>
                    <NumberInput
                        className="form-control"
                        integer
                        emptyValue={new Date().getFullYear()}
                        value={startYear}
                        onChange={(year) => setStartYear(isPlausibleYear(year) ? year : new Date().getFullYear())}
                        min={1800}
                        max={endYear}
                        disabled={showAllYears}
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Till år</Form.Label>
                    <NumberInput
                        className="form-control"
                        integer
                        emptyValue={new Date().getFullYear()}
                        value={endYear}
                        onChange={(year) => setEndYear(isPlausibleYear(year) ? year : new Date().getFullYear())}
                        min={startYear}
                        max={2100}
                        disabled={showAllYears}
                    />
                </Form.Group>

                <div className="filter-actions">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleResetFilters}
                    >
                        <X size={16} className="me-1" />
                        Återställ filter
                    </Button>
                    {isSmallScreen && (
                        <Button
                            variant="primary"
                            className="timeline-filter-ok"
                            onClick={closeFilters}
                        >
                            OK
                        </Button>
                    )}
                </div>
            </div>
        </div>

            {/* Add/Edit Custom Timeline Item Modal */}
            <Modal show={showAddModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditMode ? 'Redigera händelse' : 'Lägg till händelse'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        {!isSingleBuildingView && (
                            <Form.Group className="mb-3">
                                <Form.Label>Byggnad *</Form.Label>
                                <Form.Select
                                    value={selectedBuildingKey}
                                    onChange={(e) => setSelectedBuildingKey(e.target.value)}
                                    disabled={saving || isEditMode}
                                >
                                    <option value="">Välj byggnad...</option>
                                    {buildingOptions.map(building => (
                                        <option key={building.key} value={building.key}>
                                            {building.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Titel *</Form.Label>
                            <Form.Control
                                type="text"
                                value={newItemTitle}
                                onChange={(e) => setNewItemTitle(e.target.value)}
                                placeholder="Kort titel..."
                                disabled={saving}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Beskrivning *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={newItemDescription}
                                onChange={(e) => setNewItemDescription(e.target.value)}
                                placeholder="Beskriv händelsen..."
                                disabled={saving}
                            />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="inexact-date-toggle"
                                label="Inexakt datum"
                                checked={inexactDate}
                                onChange={(e) => setInexactDate(e.target.checked)}
                                disabled={saving}
                            />
                        </Form.Group>

                        {inexactDate ? (
                            <Form.Group className="mb-3">
                                <Form.Label>År *</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={newItemYear}
                                    onChange={(e) => setNewItemYear(e.target.value)}
                                    placeholder="YYYY"
                                    min={1000}
                                    max={9999}
                                    disabled={saving}
                                />
                            </Form.Group>
                        ) : (
                            <Form.Group className="mb-3">
                                <Form.Label>Datum *</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={newItemDate}
                                    onChange={(e) => setNewItemDate(e.target.value)}
                                    disabled={saving}
                                />
                            </Form.Group>
                        )}

                        {/* Linked components (optional) */}
                        <Form.Group className="mb-3">
                            <Form.Label>Komponenter (valfritt)</Form.Label>
                            {newItemComponentKeys.length > 0 ? (
                                <div className="d-flex flex-column gap-1 mb-2">
                                    {newItemComponentKeys.map(key => (
                                        <div key={key} className="d-flex align-items-center gap-2">
                                            <span>{pickerComponentDisplayName(key)}</span>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0 text-danger"
                                                onClick={() => setNewItemComponentKeys(prev => prev.filter(k => k !== key))}
                                                disabled={saving}
                                                aria-label="Ta bort komponent"
                                            >
                                                <X size={16} />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted small mb-2">Inga komponenter kopplade</p>
                            )}
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setShowComponentPicker(true)}
                                disabled={saving || !effectiveBuildingKey || loadingPickerBuilding || !pickerBuilding}
                            >
                                {loadingPickerBuilding ? (
                                    <><Spinner animation="border" size="sm" className="me-1" />Laddar...</>
                                ) : (
                                    <><Plus size={14} className="me-1" />Lägg till komponenter</>
                                )}
                            </Button>
                            <Form.Text className="text-muted d-block">
                                {effectiveBuildingKey
                                    ? 'Lämna tomt om händelsen inte gäller specifika komponenter'
                                    : 'Välj en byggnad först för att koppla komponenter'}
                            </Form.Text>
                        </Form.Group>

                        {/* File Attachments Section */}
                        <div
                            {...dragHandlers}
                            style={{
                                padding: '12px',
                                marginBottom: '1rem',
                                borderRadius: '8px',
                                border: isDragOver ? '2px dashed #007bff' : '2px dashed #dee2e6',
                                backgroundColor: isDragOver ? '#f0f8ff' : 'transparent',
                                transition: 'all 0.2s ease'
                            }}
                        >
                        <Form.Group>
                            <Form.Label>Bilagor</Form.Label>

                            {/* File Input */}
                            <div className="mb-2">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => setShowUploadModal(true)}
                                    disabled={saving}
                                >
                                    Ladda upp
                                </Button>
                            </div>
                            <small className="text-muted">Du kan ladda upp flera filer (bilder, dokument, etc.) eller dra och släpp dem här.</small>

                            {/* Pending Files Preview */}
                            {pendingFiles.length > 0 && (
                                <div className="mt-3">
                                    <small className="text-muted d-block mb-2">Filer att ladda upp ({pendingFiles.length}):</small>
                                    <div className="d-flex flex-wrap gap-2">
                                        {pendingFiles.map((file, index) => (
                                            <div key={index} className="d-flex align-items-center gap-2 border rounded p-2">
                                                <FileText size={16} />
                                                <span className="small">{file.name}</span>
                                                <span className="small text-muted">
                                                    ({(file.size / 1024).toFixed(1)} KB)
                                                </span>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="p-0"
                                                    onClick={() => handleRemovePendingFile(index)}
                                                    disabled={saving}
                                                >
                                                    <X size={14} />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Existing Attachments */}
                            {Object.keys(existingAttachments).length > 0 && (
                                <div className="mt-3">
                                    <small className="text-muted d-block mb-2">Befintliga bilagor:</small>
                                    <TimelineAttachments
                                        attachments={existingAttachments}
                                        buildingKey={effectiveBuildingKey}
                                        onRemove={handleRemoveExistingAttachment}
                                        showRemoveButton={true}
                                        compact={true}
                                    />
                                </div>
                            )}
                        </Form.Group>
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer className={isEditMode ? "justify-content-between" : ""}>
                    {isEditMode && (
                        <Button
                            variant="outline-danger"
                            onClick={() => requestDeleteCustomItem()}
                            disabled={saving}
                        >
                            <Trash2 size={16} className="me-1" />
                            Ta bort
                        </Button>
                    )}
                    <div className={isEditMode ? "d-flex gap-2" : "w-100 d-flex justify-content-end gap-2"}>
                        <Button
                            variant="secondary"
                            onClick={handleCloseModal}
                            disabled={saving}
                        >
                            Avbryt
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSaveCustomItem}
                            disabled={saving || !effectiveBuildingKey || !newItemTitle.trim() || !newItemDescription.trim() || (inexactDate ? !newItemYear.trim() : !newItemDate)}
                        >
                            {saving ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Sparar...
                                </>
                            ) : (
                                isEditMode ? 'Spara' : 'Lägg till'
                            )}
                        </Button>
        </div>
                </Modal.Footer>
            </Modal>

            {/* Component picker (multi-select) for linking components to a custom event */}
            {pickerBuilding && (
                <ComponentPickerModal
                    show={showComponentPicker}
                    onHide={() => setShowComponentPicker(false)}
                    building={pickerBuilding}
                    multiSelect
                    selectedKeys={newItemComponentKeys}
                    onSelectMultiple={(keys) => {
                        setNewItemComponentKeys(keys);
                        setShowComponentPicker(false);
                    }}
                    onComponentPatched={(componentKey, patch) =>
                        setPickerBuilding(prev => applyComponentPatch(prev, componentKey, patch))}
                />
            )}

            {/* Confirm removal of an attachment while editing an event */}
            <ConfirmationModal
                show={attachmentToRemove !== null}
                title="Ta bort bilaga"
                message={attachmentToRemoveName ? `Vill du ta bort bilagan ${attachmentToRemoveName}?` : 'Vill du ta bort bilagan?'}
                confirmText="Ta bort bilaga"
                onConfirm={() => { void confirmRemoveExistingAttachment(); }}
                onCancel={() => setAttachmentToRemove(null)}
            />

            {/* Confirm removal of a custom event */}
            <Modal
                show={!!deleteTargetId}
                onHide={() => { if (!saving) setDeleteTargetId(null); }}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Ta bort händelse</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Är du säker på att du vill ta bort denna händelse? Alla bilagor kommer också att tas bort.
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setDeleteTargetId(null)}
                        disabled={saving}
                    >
                        Avbryt
                    </Button>
                    <Button
                        variant="danger"
                        onClick={confirmDeleteCustomItem}
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                Tar bort...
                            </>
                        ) : (
                            <>
                                <Trash2 size={16} className="me-1" />
                                Ta bort
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Event Detail Modal (presentation). Custom events can be edited from here. */}
            <TimelineEventDetailModal
                show={showEventDetailModal}
                onHide={handleCloseEventDetailModal}
                event={selectedEvent}
                onEdit={handleEditFromDetail}
                userName={lookupUserName}
            />

            {/* Work Order Detail Modal (reused from Förvaltning) */}
            {viewingWorkOrderKey && (
                <WorkOrderDetailModal
                    show={true}
                    onHide={() => setViewingWorkOrderKey(null)}
                    workOrderKey={viewingWorkOrderKey}
                    getUserName={getUserName}
                />
            )}

            {/* Report drafts manager (admin-only, all-buildings view; mounted
                lazily so timeline visits don't fetch every draft snapshot) */}
            {isAdmin && !isSingleBuildingView && showDraftsModal && (
                <ReportDraftsManagerModal
                    show={showDraftsModal}
                    onHide={() => setShowDraftsModal(false)}
                    allEvents={allEvents}
                    buildingSummaries={buildingSummariesMap}
                />
            )}

            {/* Upload Picker Modal */}
            <UploadPickerModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                onFilesSelected={handleFiles}
                multiple={true}
                title="Ladda upp"
            />
    </div>
);
};

export default TimelinePage;
